import { useMemo } from "react";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/LayoutWithHeader.js";
import MotionInView from "../../components/MotionInView.js";
import { ReactComponent as TrackIcon } from "../../assets/IconTrack.svg";
import { ReactComponent as CurateIcon } from "../../assets/IconCurate.svg";
import { ReactComponent as ExploreIcon } from "../../assets/IconExplore.svg";
import { getStatusOptionsArray } from "../../utils/utils";
import { useAuth } from "../../context/authContext";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import useApi from "../../hooks/useApi.js";
import PosterTile from "../../components/PosterTile.js";
import { useDetailNavigation } from "../../hooks/useDetailNavigation.js";
import { useScaleGestures } from "../../hooks/useScaleGestures.js";
import animation from "../../assets/animation_arrow_green.json";
import Lottie from "lottie-react";
import ErrorIcon from "@mui/icons-material/Error";
import { useThemeContext } from "../../context/themeContext.js";
import { ONBOARDING_COLLECTION_IDS } from "../../utils/utils";

// Icon and title drift in from either side, overlap (as the drawer's marks
// do) and settle with a gentle spring, then the pair gives a slight pop.
// Kept subtle — it's a finishing touch, not the main event.
const MARK_SPRING = { type: "spring", stiffness: 320, damping: 22 };

function FirstStepMark({ icon, label, delay }) {
  const reduceMotion = useReducedMotion();
  return (
    <Stack
      component={motion.div}
      direction="row"
      alignItems="center"
      spacing={-3}
      mb={2}
      initial={reduceMotion ? false : "hidden"}
      whileInView="shown"
      viewport={{ once: true, amount: 0.6 }}
      variants={{
        hidden: { scale: 1 },
        shown: {
          scale: [1, 1.03, 1],
          transition: { delay: delay + 0.4, duration: 0.35 },
        },
      }}
    >
      <motion.div
        style={{ display: "flex" }}
        variants={{
          hidden: { x: -12, opacity: 0, scale: 0.85 },
          shown: {
            x: 0,
            opacity: 1,
            scale: 1,
            transition: { ...MARK_SPRING, delay },
          },
        }}
      >
        <SvgIcon sx={{ width: 60, height: 60, color: "primary.main" }}>
          {icon}
        </SvgIcon>
      </motion.div>
      <motion.div
        variants={{
          hidden: { x: 12, opacity: 0 },
          shown: {
            x: 0,
            opacity: 1,
            transition: { ...MARK_SPRING, delay: delay + 0.05 },
          },
        }}
      >
        <Typography variant="h5">
          <b>{label}</b>
        </Typography>
      </motion.div>
    </Stack>
  );
}

export default function Onboarding() {
  const { user } = useAuth();
  const { fetchList } = useApi();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isTouchDevice } = useThemeContext();
  const statuses = getStatusOptionsArray();
  const collectionId = ONBOARDING_COLLECTION_IDS[user.preferences.content];
  const { openContent } = useDetailNavigation();
  // Tiles show the status overlay, so have PosterTile's long press — hover
  // only, as a tap press-in would mute its pop
  const hoverGestures = useScaleGestures({ tap: false });
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // The drawer is a permanent sidebar from md up, a menu button below it
  // (HeaderMenuButton) — a layout question, so a breakpoint, not touch
  const hasMenuButton = useMediaQuery(theme.breakpoints.down("md"));

  // The starter collection is a nice-to-have: on failure show the inline error
  // below rather than letting the route error boundary replace the whole page.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["onboardingCollection", collectionId],
    queryFn: () => fetchList(collectionId),
    throwOnError: false,
  });

  const visibleStatuses = useMemo(
    () => statuses.filter((statuses) => statuses.value !== "none"),
    [statuses],
  );

  const onboardingStatuses = useMemo(() => {
    const explainerMap = {
      queued: {
        primary: "queue it",
        secondary: "add to your watchlist/to-be-read list",
      },
      started: {
        primary: "start it",
        secondary: "mark as currently watching or reading",
      },
      abandoned: {
        primary: "stop it",
        secondary: "mark as did-not-finish",
      },
      finished: {
        primary: "finish it",
        secondary: "mark as watched or read",
      },
    };

    return visibleStatuses.map((statusOption) => ({
      ...statusOption,
      ...explainerMap[statusOption.value],
    }));
  }, [visibleStatuses]);

  // The drawer's pages, in its order, for the layout guide. Profile shows
  // your avatar, as the drawer does; the rest use the app's page marks.
  const pageIconSx = { width: 28, height: 28, color: "primary.main" };
  const appPages = [
    {
      label: "Profile",
      icon: (
        <Avatar
          alt={user.givenName}
          src={user.avatar_url}
          imgProps={{ referrerPolicy: "no-referrer" }}
          sx={{ width: 28, height: 28, bgcolor: "tertiary.main" }}
        />
      ),
      description: "Your dashboard, top 5s and standouts",
    },
    {
      label: "Track",
      icon: (
        <SvgIcon sx={pageIconSx}>
          <TrackIcon />
        </SvgIcon>
      ),
      description: "Your library: everything you've tracked in one place",
    },
    {
      label: "Curate",
      icon: (
        <SvgIcon sx={pageIconSx}>
          <CurateIcon />
        </SvgIcon>
      ),
      description: "Your collections, to keep or to share",
    },
    {
      label: "Explore",
      icon: (
        <SvgIcon sx={pageIconSx}>
          <ExploreIcon />
        </SvgIcon>
      ),
      description: "Discover your next watch or read",
    },
  ];

  // The two ways to fill a new library. Track and Curate would land on empty
  // pages, and Getting started (on the dashboard, where the next session
  // opens) picks up once there's something to organise.
  const firstSteps = [
    {
      // Track's icon: an import fills the Track library
      icon: <TrackIcon />,
      label: "Import",
      description:
        "Bring your history from Letterboxd, Goodreads, StoryGraph, Trakt, TV Time or IMDb",
      path: "/import",
    },
    {
      icon: <ExploreIcon />,
      label: "Explore",
      description:
        "Explore curated collections of the best movies, books and TV shows and build your library from there",
      path: "/explore",
    },
  ];

  return (
    <Layout
      title={"You're all set! Let's get started"}
      showBackButton={false}
      showMenu={false}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <MotionInView>
          <Box
            sx={{
              p: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }}
          >
            <Typography variant="body1">
              {isTouchDevice ? "Tap" : "Hover over"} a cover to...
            </Typography>
            <List disablePadding sx={{ pl: 2 }}>
              {onboardingStatuses.map((statusOption) => (
                <ListItem
                  key={statusOption.value}
                  disableGutters
                  disablePadding
                  sx={{ alignItems: "flex-start" }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: "6px" }}>
                    {statusOption.iconFilled}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight={600}>
                        {statusOption.primary}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {statusOption.secondary}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </MotionInView>
        <MotionInView delay={0.05}>
          <Typography>
            <b>Give it a try! </b> <br />
            Tap a cover to tick off what you've already{" "}
            {user.preferences.content === "book" ? "read" : "watched"} or queue
            something you'd like to...
          </Typography>
        </MotionInView>

        {isLoading && (
          <Grid container spacing={1}>
            {Array.from({ length: isMobile ? 6 : 8 }).map((_, i) => (
              <Grid item xs={4} sm={3} key={i}>
                <Box sx={{ aspectRatio: "2/3" }}>
                  <Skeleton variant="rounded" width="100%" height="100%" />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {isError && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 2,
            }}
          >
            <ErrorIcon />
            <Typography variant="body2">
              Error loading! You can still explore the app and come back to this
              later.
            </Typography>
          </Stack>
        )}

        <MotionInView>
          {!isLoading && !isError && data?.items?.length > 0 && (
            <Grid container spacing={1}>
              {data.items.slice(0, 12).map((item) => (
                <Grid item xs={4} sm={3} key={item.content_id}>
                  <motion.div {...hoverGestures}>
                    <PosterTile
                      contentId={item.content_id}
                      contentType={data.content_type}
                      contentTitle={item.title}
                      imageUrl={item.image_url}
                      onClick={() =>
                        openContent({
                          contentId: item.content_id,
                          contentType: item.content_type,
                          title: item.title,
                        })
                      }
                      showStatus="full"
                    />
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </MotionInView>
        <MotionInView>
          <Stack direction="row" gap="20px">
            <Box
              sx={{
                width: 60,
                flexShrink: 0,
                transform: "rotate(+25deg) scaleX(1)",
                transformOrigin: "center",
              }}
            >
              <Lottie
                animationData={animation}
                loop={true}
                speed={0.1}
                style={{ width: 60, height: 60 }}
              />
            </Box>
            <Typography variant="body1" sx={{ pt: 3, maxWidth: "300px" }}>
              {isTouchDevice ? "Press and hold" : "Click"} a cover to open it -
              add notes, a rating, or explore more details
            </Typography>
          </Stack>
        </MotionInView>

        {/* A quick map of the app before the first step: the pages in the
            drawer's order, and how to get between them. This page hides the
            drawer while signing up, so it's worded for later. */}
        <MotionInView>
          <Typography variant="body1" sx={{ mt: 1 }}>
            <b>Happy curating!</b>
          </Typography>
          <Typography sx={{ mt: 1 }}>
            This is your space. It's organised as follows, and once you're in
            the app you can move between pages using the{" "}
            {hasMenuButton ? "menu button in the top left" : "sidebar"}.
          </Typography>
          <List disablePadding sx={{ mt: 1 }}>
            {appPages.map((page) => (
              <ListItem
                key={page.label}
                disableGutters
                disablePadding
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 44 }}>{page.icon}</ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight={600}>
                      {page.label}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {page.description}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </MotionInView>

        {/* Both options fill the library, so the heading says so */}
        <MotionInView>
          <Typography>
            <b>Start building your library</b>
          </Typography>
        </MotionInView>
        <MotionInView>
          <Grid container spacing={2}>
            {firstSteps.map((section, index) => (
              <Grid item xs={12} sm={6} key={section.label}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    transition: "border-color 0.2s",
                    "&:hover": {
                      borderColor: "primary.main",
                    },
                  }}
                >
                  {/* Fills the card, so its hover/press overlay covers all of it
                    even on the shorter card beside a taller one */}
                  <CardActionArea
                    onClick={() => navigate(section.path)}
                    sx={{
                      p: 2,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "stretch",
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <Stack
                        direction={{ xs: "row", sm: "column" }}
                        spacing={2}
                        alignItems={{ xs: "center", sm: "center" }}
                        justifyContent={"center"}
                      >
                        <FirstStepMark
                          icon={section.icon}
                          label={section.label}
                          delay={0.2 + index * 0.15}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {section.description}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </MotionInView>
      </Box>
    </Layout>
  );
}
